import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-route SEO tags — the single source of truth for title/description/canonical
 * and the social cards.
 *
 * WHY: index.html is served for every route, so anything hardcoded there is
 * identical on every URL. A fixed `canonical` there told Google that
 * /erstanalyse and every blog post were duplicates of "/", which kept them out
 * of the index entirely. Those tags now live here, per route, and are baked into
 * the prerendered HTML at build time (scripts/prerender.js) so crawlers and
 * social scrapers — which don't run JS — see the correct values.
 *
 * Keep index.html free of these tags: Helmet appends its own, and a duplicate
 * static tag would survive alongside it.
 *
 * Usage:  <Seo title="…" description="…" path="/erstanalyse" />
 */

export const SITE_URL = 'https://hambacore.com';
const DEFAULT_IMAGE = 'https://hamba.fra1.cdn.digitaloceanspaces.com/hamba.png';
const SITE_NAME = 'hamba GmbH';

export default function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
}) {
  // Canonical must be absolute and match exactly one URL per page.
  const canonical = `${SITE_URL}${path === '/' ? '/' : path.replace(/\/$/, '')}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <link rel="canonical" href={canonical} />
      <meta name="description" content={description} />
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="de_DE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}
